import {
  HTMLAttributes,
  ReactElement,
  forwardRef,
  isValidElement,
} from 'react';

import { Theme, WithStyles, createStyles, withStyles } from '@material-ui/core';

import classNames from 'classnames';

import { Avatar, Text } from '@backstage/ui';

import { getTypeOrNull } from '@spotify/confidence-graphql';
import { IdentityAvatar } from '@spotify/plugin-confidence-iam-react';

import Timestamp from './Timestamp';
import { useActivityFeedItemContext } from './context';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    info: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.25),
    },
    avatar: {
      width: 40,
      height: 40,
    },
    primary: {
      fontWeight: 'bold',
    },
  });

export interface HeaderProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'classes'>,
    WithStyles<typeof styles> {
  avatar?: string | ReactElement;
  primary?: string | ReactElement;
  secondary?: string | ReactElement;
}

const Header = forwardRef<HTMLDivElement, HeaderProps>((props, ref) => {
  const { activity } = useActivityFeedItemContext();
  const actor = getTypeOrNull(activity.actor);

  const {
    className,
    classes,
    avatar = <IdentityAvatar identityRef={actor ?? undefined} />,
    primary = actor?.displayName ?? actor?.name,
    secondary = <Timestamp timestamp={activity.activityTime} />,
    ...rest
  } = props;

  // Render avatar - if string, wrap in BUI Avatar
  let avatarElement = null;
  if (avatar) {
    if (typeof avatar === 'string') {
      avatarElement = (
        <Avatar src={avatar} name="" className={classes.avatar} />
      );
    } else {
      avatarElement = avatar;
    }
  }

  // Render primary - if string, wrap in Text
  const primaryElement = isValidElement(primary) ? (
    primary
  ) : (
    <Text as="span" variant="body-medium" className={classes.primary}>
      {primary}
    </Text>
  );

  // Render secondary - if string, wrap in Text
  let secondaryElement = null;
  if (secondary) {
    if (isValidElement(secondary)) {
      secondaryElement = secondary;
    } else {
      secondaryElement = (
        <Text as="span" variant="body-small" color="secondary">
          {secondary}
        </Text>
      );
    }
  }

  return (
    <div ref={ref} className={classNames(classes.root, className)} {...rest}>
      {avatarElement}
      <div className={classes.info}>
        {primaryElement}
        {secondaryElement}
      </div>
    </div>
  );
});

Header.displayName = 'ActivityFeedItem.Header';

export default withStyles(styles)(Header);
